import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';

// ----- A calmer, friendlier color system (one accent, lots of neutral) -----
const C = {
  bg: '#fffdf6',
  card: '#ffffff',
  text: '#241c0a',
  subtext: '#6b6354',
  accent: '#f2b705',
  accentDeep: '#a86b00',
  accentSoft: '#fff3cc',
  border: '#f1e8c8'
};

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ color: C.accentDeep, fontWeight: 700, letterSpacing: 1.2 }}
  >
    {children}
  </Typography>
);

const IconBadge = ({ children, size = 56 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: C.accentSoft,
      color: C.accentDeep,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 2
    }}
  >
    {children}
  </Box>
);

// A light touch of jungle — a couple of monkeys and bananas, not a chaos of them
const FloatingJungle = () => {
  const monkeys = [
    { left: '6%', top: '6%', size: 34, delay: '0s', dur: '3.6s' },
    { left: '90%', top: '10%', size: 30, delay: '0.8s', dur: '4s' }
  ];
  const bananas = [
    { left: '22%', top: '64%', size: 22, delay: '0s', dur: '3.2s' },
    { left: '78%', top: '70%', size: 20, delay: '1.1s', dur: '3.6s' },
    { left: '48%', top: '4%', size: 18, delay: '0.5s', dur: '3s' }
  ];

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {monkeys.map((m, i) => (
        <Box
          key={`monkey-${i}`}
          sx={{
            position: 'absolute',
            left: m.left,
            top: m.top,
            fontSize: m.size,
            transformOrigin: 'top center',
            animation: `swing ${m.dur} ease-in-out infinite`,
            animationDelay: m.delay
          }}
        >
          🐒
        </Box>
      ))}
      {bananas.map((b, i) => (
        <Box
          key={`banana-${i}`}
          sx={{
            position: 'absolute',
            left: b.left,
            top: b.top,
            fontSize: b.size,
            animation: `bob ${b.dur} ease-in-out infinite`,
            animationDelay: b.delay
          }}
        >
          🍌
        </Box>
      ))}
    </Box>
  );
};

const BlitzSplitLanding = () => {
  const navigate = useNavigate();

  const steps = [
    {
      icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 28 }} />,
      title: 'Send one invoice',
      description: 'Bill your client exactly like you do today — nothing changes on their end.'
    },
    {
      icon: <CallSplitOutlinedIcon sx={{ fontSize: 28 }} />,
      title: 'Set how it splits',
      description: "Tell Blitz who gets what — by amount, percentage, or however you've agreed."
    },
    {
      icon: <BoltOutlinedIcon sx={{ fontSize: 28 }} />,
      title: 'Everyone gets paid',
      description: 'Once approved, each person is paid automatically — instantly or on schedule.'
    }
  ];

  const features = [
    {
      icon: <VisibilityOutlinedIcon sx={{ fontSize: 26 }} />,
      title: 'Full visibility',
      description: "Everyone can see exactly where their payment is, so no one has to ask."
    },
    {
      icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 26 }} />,
      title: 'Approvals built in',
      description: 'Nothing pays out until the split is reviewed and approved.'
    },
    {
      icon: <PublicOutlinedIcon sx={{ fontSize: 26 }} />,
      title: 'Works across currencies',
      description: 'Pay teammates in the currency that works best for them.'
    },
    {
      icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 26 }} />,
      title: 'Flexible timing',
      description: 'Mix instant and scheduled payouts on the same invoice, no problem.'
    }
  ];

  const audiences = [
    {
      icon: <StorefrontOutlinedIcon sx={{ fontSize: 30 }} />,
      title: 'Agencies & small teams',
      description: 'Stop floating cash for contractors. Bill once, let Blitz handle the rest.'
    },
    {
      icon: <GroupsOutlinedIcon sx={{ fontSize: 30 }} />,
      title: 'Freelancers & creators',
      description: 'Know exactly when you’re getting paid — and choose to get paid sooner.'
    },
    {
      icon: <ApartmentOutlinedIcon sx={{ fontSize: 30 }} />,
      title: 'Enterprise clients',
      description: 'Keep paying your usual way. Your vendor handles the rest behind the scenes.'
    }
  ];

  const faqs = [
    {
      q: 'What is Blitz Split?',
      a: 'It’s a way to automatically divide one invoice among everyone who worked on it — so instead of collecting a payment and manually paying people out, it happens for you.'
    },
    {
      q: 'How fast is an instant payout?',
      a: 'Most instant payouts land within minutes of approval. There’s a small fee for choosing instant — scheduled payouts are free.'
    },
    {
      q: 'Can I split one invoice between several people?',
      a: 'Yes. You can split by fixed amount or by percentage, across as many people as the project needs.'
    },
    {
      q: 'What happens if something needs to change after a split is set up?',
      a: 'Splits can be edited before they’re approved. Once approved and funded, you can still see full history and status for every payout.'
    },
    {
      q: 'Does my client need to do anything differently?',
      a: 'No. They receive and pay the invoice exactly as before — the split happens entirely on your side.'
    }
  ];

  return (
    <Box sx={{ bgcolor: C.bg, color: C.text, minHeight: '100vh' }}>
      <style>{`
        @keyframes floatDot {
          0% { left: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes gentleBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        @keyframes swing {
          0%, 100% { transform: rotate(-10deg); }
          50% { transform: rotate(10deg); }
        }
        @keyframes bob {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50% { transform: translateY(-10px) rotate(4deg); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(-8deg) scale(1.05); }
          75% { transform: rotate(8deg) scale(1.05); }
        }
      `}</style>

      {/* Simple top bar */}
      <Box sx={{ borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, bgcolor: 'rgba(255,253,246,0.92)', backdropFilter: 'blur(6px)', zIndex: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
            <Typography variant="h6" fontWeight={800}>
              🍌 Blitz <Box component="span" sx={{ color: C.accentDeep }}>Split</Box>
            </Typography>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 3 }}>
              <Button onClick={() => scrollTo('how-it-works')} sx={{ color: C.text }}>How it works</Button>
              <Button onClick={() => scrollTo('payouts')} sx={{ color: C.text }}>Payouts</Button>
              <Button onClick={() => scrollTo('faq')} sx={{ color: C.text }}>FAQ</Button>
            </Box>
            <Button
              variant="contained"
              onClick={() => navigate('/onboard')}
              sx={{
                bgcolor: C.accent,
                color: C.text,
                fontWeight: 700,
                borderRadius: 99,
                px: 3,
                boxShadow: 'none',
                '&:hover': { bgcolor: C.accentDeep, color: '#fff' }
              }}
            >
              Get started
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Container maxWidth="md" sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 8 }, textAlign: 'center', position: 'relative' }}>
        <FloatingJungle />
        <Chip
          label="New: automatic invoice splitting"
          sx={{ bgcolor: C.accentSoft, color: C.accentDeep, fontWeight: 600, mb: 3 }}
        />
        <Typography variant="h2" fontWeight={800} sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, lineHeight: 1.15, mb: 3 }}>
          Invoice once.
          <br />
          Pay everyone automatically.
        </Typography>
        <Typography variant="h6" sx={{ color: C.subtext, fontWeight: 400, maxWidth: 560, mx: 'auto', mb: 5 }}>
          Blitz Split divides a single client invoice across your whole team — instantly or
          on schedule — so no one has to wait on anyone else to get paid.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/onboard')}
            sx={{
              bgcolor: C.accent,
              color: C.text,
              fontWeight: 700,
              borderRadius: 99,
              px: 4,
              py: 1.4,
              boxShadow: 'none',
              '&:hover': { bgcolor: C.accentDeep, color: '#fff' }
            }}
          >
            Create your first split
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => scrollTo('how-it-works')}
            sx={{
              borderColor: C.border,
              color: C.text,
              borderRadius: 99,
              px: 4,
              py: 1.4,
              '&:hover': { borderColor: C.accentDeep, bgcolor: C.accentSoft }
            }}
          >
            See how it works
          </Button>
        </Box>
      </Container>

      {/* Visual: money flow */}
      <Container maxWidth="md" sx={{ pb: { xs: 8, md: 10 } }}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            p: { xs: 3, md: 5 },
            bgcolor: C.card
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 3, md: 2 },
              position: 'relative'
            }}
          >
            {[
              { label: 'Your client', sub: 'Pays one invoice', icon: <ApartmentOutlinedIcon sx={{ fontSize: 30 }} /> },
              { label: 'Your business', sub: 'Keeps its share', icon: <StorefrontOutlinedIcon sx={{ fontSize: 30 }} /> },
              { label: 'Your team', sub: 'Paid automatically', icon: <GroupsOutlinedIcon sx={{ fontSize: 30 }} /> }
            ].map((node, i) => (
              <React.Fragment key={node.label}>
                <Box sx={{ textAlign: 'center', minWidth: 140 }}>
                  <IconBadge>{node.icon}</IconBadge>
                  <Typography fontWeight={700}>{node.label}</Typography>
                  <Typography variant="body2" sx={{ color: C.subtext }}>{node.sub}</Typography>
                </Box>
                {i < 2 && (
                  <Box
                    sx={{
                      flex: 1,
                      position: 'relative',
                      height: 2,
                      bgcolor: C.border,
                      width: { xs: 2, md: 'auto' },
                      minHeight: { xs: 32, md: 2 },
                      mx: { xs: 0, md: 1 },
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: C.accent,
                        animation: 'floatDot 2.6s ease-in-out infinite',
                        animationDelay: `${i * 0.6}s`
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        </Card>
      </Container>

      {/* Built by */}
      <Container maxWidth="sm" sx={{ pb: { xs: 8, md: 10 } }}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${C.border}`,
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            bgcolor: C.card,
            display: 'flex',
            alignItems: 'center',
            gap: 3,
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src="https://storage.googleapis.com/blitzccmc.appspot.com/1/IMG_3232.JPG"
              alt="Alex Roa"
              sx={{ width: 84, height: 84, border: `3px solid ${C.accent}` }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -6,
                right: -6,
                fontSize: 26,
                animation: 'wiggle 1.8s ease-in-out infinite'
              }}
            >
              🐒
            </Box>
          </Box>
          <Box>
            <Typography fontWeight={700}>Built by Alex Roa</Typography>
            <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
              Founder &amp; CEO of Blitz. Before this, he ran an agency and fronted contractor
              payouts out of his own pocket more times than he can count — Blitz Split is the
              fix he wished existed back then.
            </Typography>
          </Box>
        </Card>
      </Container>

      {/* How it works */}
      <Box id="how-it-works" sx={{ bgcolor: C.accentSoft, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>How it works</SectionLabel>
            <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
              Three steps. No spreadsheets. 🐒
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: C.card,
                    border: `1px solid ${C.border}`
                  }}
                >
                  <Typography sx={{ color: C.accentDeep, fontWeight: 800, mb: 1 }}>
                    Step {index + 1}
                  </Typography>
                  <IconBadge size={52}>{step.icon}</IconBadge>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
                    {step.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Payout options */}
      <Box id="payouts" sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Payout timing</SectionLabel>
            <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
              Everyone picks what works for them
            </Typography>
          </Box>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  height: '100%',
                  border: `2px solid ${C.accent}`,
                  bgcolor: C.card
                }}
              >
                <Chip label="Most popular for fast-moving teams" size="small" sx={{ bgcolor: C.accentSoft, color: C.accentDeep, fontWeight: 600, mb: 2 }} />
                <IconBadge>{<BoltOutlinedIcon sx={{ fontSize: 28 }} />}</IconBadge>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Get paid today
                </Typography>
                <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
                  As soon as a split is approved, funds move within minutes. A small fee applies
                  for the convenience of not waiting.
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  height: '100%',
                  border: `1px solid ${C.border}`,
                  bgcolor: C.card
                }}
              >
                <Chip label="No fee" size="small" sx={{ bgcolor: '#eef2ee', color: '#3a5a40', fontWeight: 600, mb: 2 }} />
                <IconBadge>{<CalendarMonthOutlinedIcon sx={{ fontSize: 28 }} />}</IconBadge>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Schedule it
                </Typography>
                <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
                  Choose a 15, 30, or 60-day payout window with zero added cost — funds arrive
                  automatically on the date you set.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ bgcolor: C.accentSoft, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Why teams stick with it</SectionLabel>
            <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
              Built so nobody has to chase anyone
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} md={3} key={feature.title}>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <IconBadge size={52}>{feature.icon}</IconBadge>
                  <Typography fontWeight={700} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Who it's for */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Who it's for</SectionLabel>
            <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
              Made for everyone in the chain
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {audiences.map((a) => (
              <Grid item xs={12} md={4} key={a.title}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: `1px solid ${C.border}`,
                    bgcolor: C.card,
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)', animation: 'gentleBounce 1.2s ease-in-out' }
                  }}
                >
                  <IconBadge>{a.icon}</IconBadge>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {a.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
                    {a.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ */}
      <Box id="faq" sx={{ bgcolor: C.accentSoft, py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <SectionLabel>Questions</SectionLabel>
            <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mt: 1 }}>
              Good to know
            </Typography>
          </Box>
          {faqs.map((item) => (
            <Accordion
              key={item.q}
              elevation={0}
              sx={{
                bgcolor: C.card,
                borderRadius: 3,
                mb: 1.5,
                border: `1px solid ${C.border}`,
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: C.accentDeep }} />}>
                <Typography fontWeight={700}>{item.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: C.subtext, lineHeight: 1.7 }}>
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 2 }}>
          Ready to stop splitting invoices by hand? 🍌
        </Typography>
        <Typography variant="body1" sx={{ color: C.subtext, mb: 4 }}>
          Set up your first split in a few minutes — no contracts to renegotiate, no new tools
          for your clients to learn.
        </Typography>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/onboard')}
          sx={{
            bgcolor: C.accent,
            color: C.text,
            fontWeight: 700,
            borderRadius: 99,
            px: 5,
            py: 1.5,
            boxShadow: 'none',
            '&:hover': { bgcolor: C.accentDeep, color: '#fff' }
          }}
        >
          Get started with Blitz Split
        </Button>
      </Container>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${C.border}`, py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" sx={{ color: C.subtext }}>
            © 2026 Blitz Systems Corp — Blitz Split
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default BlitzSplitLanding;